"use client";

import * as React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const Word = ({ children, progress, range, freezeProgress, isActive, isCompleted }) => {
  // Calculate color based on freeze progress or scroll progress
  const scrollColor = useTransform(progress, range, ["#d4d4d4", "#171717"]);

  // Determine the actual color to use
  const [currentColor, setCurrentColor] = React.useState("#d4d4d4");

  // Handle freeze-frame progress
  React.useEffect(() => {
    if (isCompleted) {
      // Section completed - all text should be fully highlighted (stay dark)
      setCurrentColor("#171717");
      return;
    }

    if (isActive && freezeProgress !== undefined) {
      // During freeze-frame, use freeze progress
      const [start, end] = range;
      const rangeSize = end - start;
      const wordProgress = Math.max(0, Math.min(1, (freezeProgress - start) / rangeSize));

      // Interpolate color
      const gray = 212; // #d4d4d4
      const dark = 23;  // #171717
      const value = Math.round(gray - (gray - dark) * wordProgress);
      setCurrentColor(`rgb(${value}, ${value}, ${value})`);
    }
  }, [freezeProgress, isActive, isCompleted, range]);

  // Subscribe to scroll-based color changes ONLY when not in freeze mode and not completed
  React.useEffect(() => {
    // Only use scroll-based animation if section is not active and not completed
    if (isActive || isCompleted) {
      return; // Don't subscribe to scroll changes
    }

    const unsubscribe = scrollColor.on("change", (value) => {
      setCurrentColor(value);
    });

    return () => unsubscribe();
  }, [scrollColor, isActive, isCompleted]);

  return (
    <motion.span
      style={{ color: currentColor }}
      className="inline-block mr-[0.2em] text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold leading-[1.35]"
    >
      {children}
    </motion.span>
  );
};

export const MagicText = ({ text, freezeProgress, isActive, isCompleted }) => {
  const container = useRef(null);

  // Scroll-based animation as fallback when not in freeze mode
  // These offsets are for fallback only - freeze-frame uses freezeProgress prop
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start 0.6", "start 0.2"],
  });

  const words = text.split(" ");

  return (
    <p ref={container} className="flex flex-wrap leading-[1.4] py-2">
      {words.map((word, i) => {
        const start = i / words.length;
        const end = start + 1 / words.length;

        return (
          <Word
            key={i}
            progress={scrollYProgress}
            range={[start, end]}
            freezeProgress={freezeProgress}
            isActive={isActive}
            isCompleted={isCompleted}
          >
            {word}
          </Word>
        );
      })}
    </p>
  );
};
